/**
 * Shared design-system primitives (see ../../DESIGN.md). Kept in one small module so the
 * Phase 3 dashboard and Phase 6 admin can import the same Button / Tile / Chip /
 * StatusPill / Field / Spinner and the product feels like one tool.
 */
import type { ButtonHTMLAttributes, HTMLAttributes, KeyboardEvent, ReactNode } from 'react';
import { plainStatus } from '../lib/status';

type ButtonVariant = 'primary' | 'secondary' | 'ghost';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  block?: boolean;
}

export function Button({ variant = 'primary', block, className, children, ...rest }: ButtonProps) {
  const classes = ['btn', `btn--${variant}`, block ? 'btn--block' : '', className ?? '']
    .filter(Boolean)
    .join(' ');
  return (
    <button className={classes} {...rest}>
      {children}
    </button>
  );
}

interface TileProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
}

export function Tile({ children, className, ...rest }: TileProps) {
  return (
    <div className={['tile', className ?? ''].filter(Boolean).join(' ')} {...rest}>
      {children}
    </div>
  );
}

export function Chip({ children }: { children: ReactNode }) {
  return <span className="chip">{children}</span>;
}

/** Plain-language status pill for a report (colour lives only on the pill). */
export function StatusPill({ status }: { status: string }) {
  const s = plainStatus(status);
  return (
    <span className={`status status--${s.tone}`}>
      <span className="status__glyph" aria-hidden="true">
        {s.glyph}
      </span>
      {s.label}
    </span>
  );
}

export function Spinner({ label }: { label?: string }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
      <span className="spinner" aria-hidden="true" />
      {label ? <span className="muted">{label}</span> : null}
      <span className="sr-only">Loading</span>
    </span>
  );
}

/** Spread onto a div/tile to make it clickable + keyboard-activatable (Enter/Space). */
export function activatable(onClick: () => void) {
  return {
    role: 'button' as const,
    tabIndex: 0,
    onClick,
    onKeyDown: (e: KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onClick();
      }
    },
  };
}

export function Banner({ tone, children }: { tone: 'error' | 'info' | 'good'; children: ReactNode }) {
  return (
    <div className={`banner banner--${tone}`} role={tone === 'error' ? 'alert' : 'status'}>
      {children}
    </div>
  );
}
